import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'
import toast from 'react-hot-toast'
import { HiOutlineArrowLeft, HiOutlineSave } from 'react-icons/hi'
import useDocumentTitle from '../../hooks/useDocumentTitle'

export default function AgencyFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  useDocumentTitle(isEdit ? 'Edit Agency' : 'Create Agency')

  const [name, setName] = useState('')
  const [loading, setLoading] = useState(isEdit)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isEdit) {
      fetchAgency()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function fetchAgency() {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      setName(data.name || '')
    } catch (err) {
      toast.error('Agency not found')
      navigate('/agencies')
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!name.trim()) {
      setError('Agency name is required')
      return
    }

    setError('')
    setSubmitting(true)
    try {
      if (isEdit) {
        const { error } = await supabase
          .from('agencies')
          .update({ name: name.trim() })
          .eq('id', id)

        if (error) throw error
        toast.success('Agency updated successfully')
      } else {
        const { error } = await supabase
          .from('agencies')
          .insert([{ name: name.trim() }])

        if (error) throw error
        toast.success('Agency created successfully')
      }

      navigate('/agencies')
    } catch (err) {
      toast.error(err.message || 'Failed to save agency')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="agency-form-page">
      <div className="page-header">
        <div>
          <button
            className="btn btn-ghost back-btn"
            onClick={() => navigate('/agencies')}
          >
            <HiOutlineArrowLeft />
            Back to Agencies
          </button>
          <h1>{isEdit ? 'Edit Agency' : 'Create New Agency'}</h1>
          <p>
            {isEdit
              ? 'Update agency information'
              : 'Fill in the details to register a new agency'}
          </p>
        </div>
      </div>

      <form className="form-card" onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>General Information</h3>
          <div className="form-grid">
            <div className={`form-group full-width ${error ? 'has-error' : ''}`}>
              <label htmlFor="name">
                Agency Name <span className="required">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (error) setError('')
                }}
                placeholder="e.g. Century 21 Paris"
                disabled={submitting}
                autoFocus
              />
              {error && <span className="form-error">{error}</span>}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/agencies')}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="loading-spinner small" />
                Saving...
              </>
            ) : (
              <>
                <HiOutlineSave />
                {isEdit ? 'Update Agency' : 'Create Agency'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
